/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ShopArticleItem} from "types/entities/ShopArticleItem";
import {currencyNumberFormat} from "utils/utility";
import {Article} from "types/entities/Article";
import {ArticleType} from "types/enums/ArticleType";
import ShopAction from "store/shop/ShopAction";
import {useDispatch} from "useDispatch";
import {ThunkAction} from "redux-thunk";
import {ArticleItem} from "types/entities/ArticleItem";
import {useTypedSelector} from "helpers/reducers";
import {PersonType} from "types/enums/PersonType";
import {CustomGift} from "types/entities/CustomGift";

export const shopArticlePrice = (shopArticleItem: ShopArticleItem) => {
    if (shopArticleItem.article_id === 9000) return currencyNumberFormat(shopArticleItem.price);
    return currencyNumberFormat(shopArticleItem.article.article_value / shopArticleItem.quantity);
};

export const articlePrice = (count, personType, articles): string => {
    const article = findArticle(count, personType, articles);
    if (!article) {
        return "";
    }
    if (count === 0) {
        count = 1;
    }
    return currencyNumberFormat(article.article_value / count);
};

export const filterArticlesByType = (articles: Article[], articleType: ArticleType, activity_id: number) => {
    return articles
        .filter(article => {
            return article.type === articleType;
        })
        .filter(article => article.activity_id === activity_id);
};

export const findArticle = (count, personType, articles): Article | null => {
    const article = articles.find(article => {
        if (count === 0) {
            count = 1;
        }
        return article.race_count_total === count && article.person_type === personType;
    });
    if (!article) {
        return null;
    }
    return article;
};

export const useShopItemsCartHook = () => {
    const dispatch = useDispatch();
    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);

    const resetCart = () => {
        dispatch(ShopAction.resetShoppingCart());
    };
    const removeFromCart = (shopArticleItem: ShopArticleItem) => {
        dispatch<ThunkAction<any, any, any, any>>(ShopAction.fetchDeleteShopArticleItem(shopArticleItem)).then(r => {
            dispatch(ShopAction.getShopArticleItemsCart(shopArticleItem.shop_article_items_cart_id));
            dispatch(ShopAction.deleteShopArticleItem(shopArticleItem));
        });
    };

    const getCart = shop_article_items_cart_id => {
        dispatch(ShopAction.getShopArticleItemsCart(shop_article_items_cart_id));
    };

    const createCart = profile => {
        dispatch(ShopAction.createShopArticleItemsCart(profile));
    };
    const addToCart = (count, personType: PersonType, articles) => {
        const article = findArticle(count, personType, articles);
        if (count === 0) {
            return;
        }
        if (!article) {
            return;
        }
        const articleItem: ArticleItem = {
            article_id: article.article_id,
            articleType: article.type,
            quantity: 1,
            personType: personType,
        } as ArticleItem;

        if (itemsCart.shopArticleItemsCart) {
            dispatch(ShopAction.addShopArticleItem(articleItem, itemsCart.shopArticleItemsCart));
        }
    };

    const addArticleToCart = (article: Article) => {
        if (!article) {
            return;
        }
        const articleItem: ArticleItem = {
            article_id: article.article_id,
            articleType: article.type,
            quantity: 1,
            personType: PersonType.Adult,
        } as ArticleItem;

        if (itemsCart.shopArticleItemsCart) {
            dispatch(ShopAction.addShopArticleItem(articleItem, itemsCart.shopArticleItemsCart));
        }
    };

    const addCustomGiftToCart = (price: number) => {
        if (!price) {
            return;
        }

        const giftItem: CustomGift = {
            price: price,
            quantity: 1,
        };

        if (itemsCart.shopArticleItemsCart) {
            dispatch(ShopAction.addCustomGiftItem(giftItem, itemsCart.shopArticleItemsCart));
        }
    };

    return {
        resetCart,
        removeFromCart,
        addToCart,
        addArticleToCart,
        createCart,
        getCart,
        addCustomGiftToCart,
    };
};
